import {ToastContent} from "components";
import {settledImportUrlStatus, withImportErrorReason} from "helpers/importErrorReason";
import {toast} from "react-toastify";

/**
 * The failed end of a single-url LinkedIn import (filter search, project add, profile refresh).
 *
 * The polling actions and the create actions all need the same answer to "has this import failed,
 * and what do we tell the user?". A row with an error_reason has failed even while its status still
 * reads Created / In Progress (helpers/importErrorReason), and that can already be true in the
 * create response, before any watcher has started.
 */

type TImportRow = {
	status: string;
	error_reason?: string | null;
};

export type TFailedImportOutcome = {
	candidate: null;
	status: string;
	message: string;
	error: true;
};

const FAILED_STATUSES = ["Scraping Error", "Import Error"];

/** The outcome the watchers act on when the import has failed; null while it has not. */
export const failedImportOutcome = (row: TImportRow): TFailedImportOutcome | null => {
	const status = settledImportUrlStatus(row);

	if (!FAILED_STATUSES.includes(status)) {
		return null;
	}

	return {
		candidate: null,
		status,
		message: withImportErrorReason("This url could not be processed", row.error_reason),
		error: true,
	};
};

/**
 * Tells the user an import failed, in the words its watcher would have used ("Candidate Import" for
 * a search, "CandidateDetails Import" for a profile refresh). For an import that is already failed
 * when it is created, because no watcher will ever start for it.
 */
export const fireImportFailedToaster = (prefix: string, outcome: TFailedImportOutcome) => {
	toast.info(ToastContent, {
		autoClose: false,
		closeOnClick: true,
		data: {title: `${prefix}: ${outcome.message}`},
	});
};
